import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Coffee, CheckCircle2, PenLine, MapPin, CheckCircle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import VerifiedBadge from "@/components/VerifiedBadge";
import RatingForm, { EligibilityList } from "@/components/ratings/RatingForm";
import api, { formatApiError } from "@/lib/api";

function LinkAccount({ sid, pid, onDone }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setOpts([]); return; }
    const t = setTimeout(() => api.get("/ratings/directory", { params: { q, sort: "name" } }).then(({ data }) => setOpts(data.slice(0, 6))).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const pick = async (uid) => {
    try { await api.patch(`/casual/sessions/${sid}/players/${pid}`, { user_id: uid }); toast.success("Linked to account"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="relative">
      <input data-testid={`casual-link-${pid}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Link account…" className="rounded-lg glass px-2 py-1 text-xs w-36" style={{ color: "var(--text)" }} />
      {opts.length > 0 && (
        <div className="absolute right-0 z-20 mt-1 w-56 rounded-xl glow-border p-1" style={{ background: "var(--surface)" }}>
          {opts.map((o) => <button key={o.user_id} data-testid={`casual-link-opt-${o.user_id}`} onClick={() => pick(o.user_id)} className="w-full text-left text-xs px-2 py-1.5 rounded-lg hover:bg-black/10">{o.name} <span className="mono" style={{ color: "var(--text-2)" }}>{o.player_code}</span></button>)}
        </div>
      )}
    </div>
  );
}

function RateCasual({ sid, player, onBack }) {
  const [el, setEl] = useState(null);
  const [done, setDone] = useState(null);
  useEffect(() => { api.get(`/casual/sessions/${sid}/eligibility/${player.user_id}`).then(({ data }) => setEl(data)).catch(() => setEl(false)); }, [sid, player.user_id]);
  if (el === null) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>;
  if (done) return (
    <div className="text-center py-10" data-testid="casual-rating-success">
      <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "#22c55e" }} />
      <h2 className="font-display font-black text-xl mt-3">Casual rating submitted</h2>
      <button data-testid="casual-back-btn" onClick={onBack} className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold mt-5">Rate another player</button>
    </div>
  );
  return (
    <div>
      <button data-testid="casual-back-link" onClick={onBack} className="text-xs font-bold text-primary-c mb-4">← Back to session</button>
      <h2 className="font-display font-black text-xl mb-4">Rate {player.name}'s casual play</h2>
      {el && el.eligible
        ? <RatingForm playerId={player.user_id} el={el} casual submitUrl={`/casual/sessions/${sid}/rate`} onDone={setDone}
            note={<>This is a <b>Casual Play</b> rating for {el.session.name}. It builds a separate Casual Play Rating and does not count in the 50/30/20 Assessment.</>} />
        : el ? <EligibilityList el={el} /> : <p className="text-sm">Could not check eligibility.</p>}
    </div>
  );
}

export default function CasualSession() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [rating, setRating] = useState(null);
  const load = useCallback(() => api.get(`/casual/sessions/${sid}`).then(({ data }) => setS(data)).catch((e) => setErr(formatApiError(e.response?.data?.detail))), [sid]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Casual Play" />
      <div className="max-w-3xl mx-auto px-4 py-8">
        {err ? <p className="text-sm text-center py-20" data-testid="casual-error">{err}</p> : !s ? <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div> : (
          <div className="rounded-3xl glow-border p-6" style={{ background: "var(--surface)" }} data-testid="casual-session-page">
            {rating ? <RateCasual sid={sid} player={rating} onBack={() => { setRating(null); load(); }} /> : (
              <>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-primary-c"><Coffee className="w-4 h-4" /> Casual Play session</div>
                <h1 className="font-display font-black text-2xl mt-1 break-words" data-testid="casual-session-name">{s.name}</h1>
                <div className="text-xs mt-1 flex items-center gap-2" style={{ color: "var(--text-2)" }}>{s.date}{s.location && <><MapPin className="w-3 h-3" />{s.location}</>} · {s.games} games · {s.players.length} players</div>
                {!s.participant && s.is_host && <p className="text-xs mt-3 rounded-xl px-3 py-2" style={{ background: "rgba(245,158,11,0.12)", color: "#b45309" }}>Link yourself to a player below to rate others in this session.</p>}
                <div className="mt-5 divide-y" style={{ borderColor: "var(--border)" }}>
                  {s.players.map((p) => (
                    <div key={p.pid} className="py-2.5 flex items-center gap-2 text-sm flex-wrap" data-testid={`casual-player-${p.pid}`}>
                      <span className="font-semibold">{p.name}</span>{p.user_id && <VerifiedBadge verified={p.verified} />}
                      {p.is_me && <span className="text-[10px] mono uppercase glass rounded-full px-2">you</span>}
                      <span className="text-[11px] mono" style={{ color: "var(--text-2)" }}>{p.games} games</span>
                      <span className="ml-auto flex items-center gap-2">
                        {!p.user_id && <span className="text-[10px] mono uppercase" style={{ color: "#b45309" }}>no account linked</span>}
                        {s.is_host && !p.user_id && <LinkAccount sid={sid} pid={p.pid} onDone={load} />}
                        {p.user_id && !p.is_me && p.rated && <span className="text-xs flex items-center gap-1" style={{ color: "#22c55e" }}><CheckCircle className="w-3.5 h-3.5" />Rated</span>}
                        {p.user_id && !p.is_me && !p.rated && s.participant && <button data-testid={`casual-rate-${p.user_id}`} onClick={() => setRating(p)} className="btn-glow rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1"><PenLine className="w-3 h-3" />Rate</button>}
                        {p.user_id && <button data-testid={`casual-profile-${p.user_id}`} onClick={() => nav(`/players/${p.user_id}`)} className="text-xs text-primary-c font-bold">Profile</button>}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
